import { APIConfiguration } from '@savgroup-front-common/configuration';
import {
  CIVILITY,
  COMMON_QUERY_PARAMS,
  CURRENCIES,
  HOUSING_TYPES,
  PAYMENT_BEHAVIOUR,
  SUPPORTED_METHODS,
} from '@savgroup-front-common/constants/src/shared';
import {
  BackResponse,
  BackResponseFailure,
} from '@savgroup-front-common/types';

import { prepareResponseFailure } from '../../helpers';
import { apiCall } from '../../services';

import { TransportMethodType } from './getClaimGroupTransportPlanQuery';

const ENDPOINT = `${APIConfiguration.claim}claimGroups/${COMMON_QUERY_PARAMS.CLAIM_GROUP_ID}/confirmation`;

export interface Address {
  addressId: string;
  civility: CIVILITY;
  firstname: string;
  lastname: string;
  address: string;
  additionalAddress: string;
  housingType: HOUSING_TYPES;
  doorCode: string;
  floor: string;
  hasElevator: boolean;
  hasParkingSpace: boolean;
  postalCode: string;
  city: string;
  countryCode: string;
  phone: string;
  companyName: string;
  additionalInformation: string;
  isDefault: boolean;
}

export interface ClaimGroupConfirmationSummary {
  claimGroupId: string;
  solutionTypeName: string;
  solutionPrice: number;
  solutionPriceCurrencyCode: string;
  solutionPriceWithTax: number;
  solutionPriceWithTaxCurrencyCode: string;
  solutionGroupId: string;
  depositStoreId: string;
  purchaseStoreId: string;
  storeActorId: string;
  actorId: string;
  actorType: string;
  depositAddress: Address;
  deliveryAddress: Address;
  carrierDepositName: string;
  carrierDeliveryName: string;
  carrierDeliveryPrice: number;
  carrierDeliveryPriceCurrencyCode: string;
  carrierDepositPrice: number;
  carrierDepositPriceCurrencyCode: string;
  carrierDeliveryPriceWithTax: number;
  carrierDeliveryPriceWithTaxCurrencyCode: string;
  carrierDepositPriceWithTax: number;
  carrierDepositPriceWithTaxCurrencyCode: string;

  deliveryTransportPlanPrice: number;
  deliveryTransportPlanPriceCurrencyCode: CURRENCIES;
  deliveryTransportPlanPriceWithVat: number;
  deliveryTransportPlanPriceWithVatCurrencyCode: CURRENCIES;
  deliveryTransportPlanMethodType: TransportMethodType;
  deliveryTransportPlanOperator: string;
  deliveryTransportPlanService: string;
  deliveryTransportPlanUniqueName: string;

  depositTransportPlanPrice: number;
  depositTransportPlanPriceCurrencyCode: CURRENCIES;
  depositTransportPlanPriceWithVat: number;
  depositTransportPlanPriceWithVatCurrencyCode: CURRENCIES;
  depositTransportPlanMethodType: TransportMethodType;
  depositTransportPlanOperator: string;
  depositTransportPlanService: string;
  depositTransportPlanUniqueName: string;

  claims: {
    claimId: string;
    issueName: string;
    reasonName: string;
    comments: {
      type: string;
      content: string;
    }[];
    documents: {
      name: string;
      originalFileName: string;
      extension: string;
      url: string;
      id: string;
      uploaderId: string;
      uploaderFullName: string;
      uploaderType: string;
      uploadDateUtc: Date;
      customerVisibility: boolean;
    }[];
    solutionTypeName: string;
    solutionTypeKey: string;
    solutionPrice: number;
    solutionPriceCurrency: string;
    solutionGroupId: string;
    additionalClaimInformation: {
      additionalInformationId: string;
      additionalInformationEnumValue: string;
      additionalInformationStringValue: string;
      additionalInformationFilesValue?: {
        blobName: string;
        customerVisibility: boolean;
        isUploadedByOwner: boolean;
        uploadDateUtc: string;
        uploaderFullName: string;
        uploaderId: string;
        fileName: string;
        url: string;
      }[];
      additionalInformationFileValue: {
        fileName: string;
        url: string;
        uploaderId: string;
        uploaderFullName: string;
        isUploadedByOwner: boolean;
        uploadDateUtc: Date;
        customerVisibility: boolean;
      };
    }[];
  }[];
  shouldGenerateAndPayInvoice: boolean;
  paymentBehavior: PAYMENT_BEHAVIOUR;
  carrierDepositReach: string;
  carrierDeliveryReach: string;
  carrierDepositProductType: string;
  carrierDeliveryProductType: string;
}

export interface GetConfirmationInfoByClaimGroupArgs {
  claimGroupId: string;
}

export const getClaimGroupConfirmationQuery = async ({
  claimGroupId,
}: GetConfirmationInfoByClaimGroupArgs): Promise<
  BackResponse<ClaimGroupConfirmationSummary> | BackResponseFailure
> => {
  try {
    const url = ENDPOINT.replace(
      COMMON_QUERY_PARAMS.CLAIM_GROUP_ID,
      claimGroupId,
    );

    const response = await apiCall<BackResponse<ClaimGroupConfirmationSummary>>(
      url,
      {
        method: SUPPORTED_METHODS.GET,
      },
    );

    return response;
  } catch (exception) {
    return prepareResponseFailure(exception);
  }
};
